// ═══════════════════════════════════════════════════════
// CONSTANTS — Valores de configuração extraídos de componentes
// ═══════════════════════════════════════════════════════
// Sprint 9: movidos de inline em páginas para centralizar manutenção.
// Valores que dependem de snapshot do cliente devem migrar para
// Supabase (clients_meta ou snapshots) em sprint futuro.

// ── Type Interfaces ──────────────────────────────────

export interface TargetAllocation {
  reserva: number;
  pos: number;
  ipca: number;
  pre: number;
  credpriv: number;
  multi: number;
  rv: number;
  inter: number;
  prev: number;
}

export interface TaxBracket {
  label: string;
  pct: number;
  dias: number;
}

export interface InvestmentTaxInfo {
  categoria: string;
  iof: boolean;
  comeCotas: boolean;
  comeCotasPct?: number;
  nota: string;
  isencao?: string;
  faixas: TaxBracket[];
}

export interface AllocClass {
  id: string;
  label: string;
  pct: number;
  val: number;
  cor: string;
  desc: string;
}

export interface RetirementScenario {
  ret: number;
  cor: string;
  desc: string;
  alocDesc: string;
}

/**
 * Alocação-alvo por classe de ativo para rebalanceamento.
 * Chaves = IDs de ALLOC_CLASSES para garantir consistência entre
 * Motor de Rebalanceamento (CarteiraPage) e Simulador de Aportes.
 * Usado em RebalancingSection.jsx.
 * @deprecated Migrado para clients_meta.target_allocation (por cliente). Mantido como fallback.
 */
export const TARGET_ALLOCATION: TargetAllocation = {
  reserva: 5,
  pos: 15,
  ipca: 18,
  pre: 7,
  credpriv: 5,
  multi: 12,
  rv: 20,
  inter: 8,
  prev: 10,
};

/**
 * Tabela de IR por tipo de investimento.
 * Legislação base:
 * · Lei 11.033/2004 — tabela regressiva de RF
 * · Lei 11.053/2004 — previdência complementar (regressiva)
 * · Lei 12.431/2011 — isenção de CRI/CRA/debêntures incentivadas
 * · Lei 14.754/2023 — ETFs de renda fixa (alíquota única 15%)
 * · IN RFB 1.585/2015 — COE
 * · IN RFB 1.888/2019 — criptoativos
 * · Decreto 6.306/2007 — IOF regressivo
 *
 * Cada entrada contém: categoria, faixas, flags (iof, comeCotas), notas legais e isenções.
 * Usado em CalcIRPage.jsx.
 */
export const IR_TABELA: Record<string, InvestmentTaxInfo> = {
  // ─── RENDA FIXA ────────────────────────────────────────────────────────
  "CDB / LC / RDB": {
    categoria: "Renda Fixa",
    iof: true,
    comeCotas: false,
    nota: "Tabela regressiva (Lei 11.033/2004). IOF para resgates < 30 dias.",
    faixas: [
      { label: "Até 180 dias", pct: 22.5, dias: 180 },
      { label: "181 a 360 dias", pct: 20.0, dias: 360 },
      { label: "361 a 720 dias", pct: 17.5, dias: 720 },
      { label: "Acima de 720 dias", pct: 15.0, dias: 9999 },
    ],
  },
  "Tesouro Direto (Selic / IPCA+ / Pré)": {
    categoria: "Renda Fixa",
    iof: true,
    comeCotas: false,
    nota: "Tabela regressiva (Lei 11.033/2004). IOF para resgates < 30 dias.",
    faixas: [
      { label: "Até 180 dias", pct: 22.5, dias: 180 },
      { label: "181 a 360 dias", pct: 20.0, dias: 360 },
      { label: "361 a 720 dias", pct: 17.5, dias: 720 },
      { label: "Acima de 720 dias", pct: 15.0, dias: 9999 },
    ],
  },
  "LCI / LCA": {
    categoria: "Renda Fixa",
    iof: false,
    comeCotas: false,
    nota: "Isentos de IR para pessoa física (Lei 11.033/2004, art. 3°).",
    faixas: [{ label: "Isento para PF", pct: 0, dias: 0 }],
  },
  "CRI / CRA": {
    categoria: "Renda Fixa",
    iof: false,
    comeCotas: false,
    nota: "Isentos de IR para pessoa física (Lei 12.431/2011).",
    faixas: [{ label: "Isento para PF", pct: 0, dias: 0 }],
  },
  "Debêntures Incentivadas": {
    categoria: "Renda Fixa",
    iof: false,
    comeCotas: false,
    nota: "Isentas de IR para PF (Lei 12.431/2011, art. 2°).",
    faixas: [{ label: "Isento para PF", pct: 0, dias: 0 }],
  },
  "Debêntures Comuns": {
    categoria: "Renda Fixa",
    iof: true,
    comeCotas: false,
    nota: "Tabela regressiva (Lei 11.033/2004). IOF para resgates < 30 dias.",
    faixas: [
      { label: "Até 180 dias", pct: 22.5, dias: 180 },
      { label: "181 a 360 dias", pct: 20.0, dias: 360 },
      { label: "361 a 720 dias", pct: 17.5, dias: 720 },
      { label: "Acima de 720 dias", pct: 15.0, dias: 9999 },
    ],
  },
  COE: {
    categoria: "Renda Fixa",
    iof: false,
    comeCotas: false,
    nota: "Tabela regressiva sobre o rendimento (IN RFB 1.585/2015).",
    faixas: [
      { label: "Até 180 dias", pct: 22.5, dias: 180 },
      { label: "181 a 360 dias", pct: 20.0, dias: 360 },
      { label: "361 a 720 dias", pct: 17.5, dias: 720 },
      { label: "Acima de 720 dias", pct: 15.0, dias: 9999 },
    ],
  },

  // ─── FUNDOS DE INVESTIMENTO ────────────────────────────────────────────
  "Fundos RF / Multimercado (Longo Prazo)": {
    categoria: "Fundos",
    iof: true,
    comeCotas: true,
    comeCotasPct: 15,
    nota: "Tabela regressiva + come-cotas semestral de 15% (maio/nov). IOF < 30 dias.",
    faixas: [
      { label: "Até 180 dias", pct: 22.5, dias: 180 },
      { label: "181 a 360 dias", pct: 20.0, dias: 360 },
      { label: "361 a 720 dias", pct: 17.5, dias: 720 },
      { label: "Acima de 720 dias", pct: 15.0, dias: 9999 },
    ],
  },
  "Fundos Curto Prazo": {
    categoria: "Fundos",
    iof: true,
    comeCotas: true,
    comeCotasPct: 20,
    nota: "Come-cotas semestral de 20%. Máximo: 22,5% até 180 dias, 20% acima.",
    faixas: [
      { label: "Até 180 dias", pct: 22.5, dias: 180 },
      { label: "Acima de 180 dias", pct: 20.0, dias: 9999 },
    ],
  },
  "Fundos de Ações (FIA)": {
    categoria: "Fundos",
    iof: false,
    comeCotas: false,
    nota: "Alíquota fixa de 15% sobre o ganho. Sem come-cotas (Lei 11.033/2004).",
    faixas: [{ label: "Alíquota fixa", pct: 15.0, dias: 0 }],
  },

  // ─── RENDA VARIÁVEL ────────────────────────────────────────────────────
  "Ações (Swing Trade)": {
    categoria: "Renda Variável",
    iof: false,
    comeCotas: false,
    nota: "15% sobre lucro líquido. Prejuízo compensável com ganhos futuros.",
    isencao: "Vendas ≤ R$ 20.000/mês: isento para PF",
    faixas: [{ label: "Ganho de capital", pct: 15.0, dias: 0 }],
  },
  "Ações (Day Trade)": {
    categoria: "Renda Variável",
    iof: false,
    comeCotas: false,
    nota: "20% sobre lucro em day trade. IR retido na fonte: 1%. Sem isenção de R$ 20k.",
    faixas: [{ label: "Day Trade", pct: 20.0, dias: 0 }],
  },
  "FII (Fundo Imobiliário)": {
    categoria: "Renda Variável",
    iof: false,
    comeCotas: false,
    nota: "Ganho de capital na venda: 20%. Dividendos isentos para PF (Lei 8.668/1993, art. 3°-A, min. 50 cotistas).",
    isencao: "Dividendos: isentos para PF (mín. 50 cotistas, cotas em bolsa)",
    faixas: [{ label: "Ganho de capital", pct: 20.0, dias: 0 }],
  },
  "ETF Renda Variável": {
    categoria: "Renda Variável",
    iof: false,
    comeCotas: false,
    nota: "15% sobre ganho de capital na venda. Sem isenção de R$ 20k/mês.",
    faixas: [{ label: "Ganho de capital", pct: 15.0, dias: 0 }],
  },
  "ETF Renda Fixa": {
    categoria: "Renda Variável",
    iof: false,
    comeCotas: false,
    nota: "Alíquota única de 15% (Lei 14.754/2023). Sem come-cotas.",
    faixas: [{ label: "Alíquota única", pct: 15.0, dias: 0 }],
  },
  "BDR / Ações Exterior": {
    categoria: "Renda Variável",
    iof: false,
    comeCotas: false,
    nota: "15% swing trade / 20% day trade. Sem isenção de R$ 20k/mês para BDRs.",
    faixas: [
      { label: "Swing Trade", pct: 15.0, dias: 0 },
      { label: "Day Trade", pct: 20.0, dias: 0 },
    ],
  },

  // ─── PREVIDÊNCIA PRIVADA ───────────────────────────────────────────────
  "Previdência (Tabela Regressiva)": {
    categoria: "Previdência",
    iof: false,
    comeCotas: false,
    nota: "Tabela regressiva definitiva (Lei 11.053/2004). PGBL: incide sobre total. VGBL: só rendimento.",
    faixas: [
      { label: "Até 2 anos", pct: 35, dias: 730 },
      { label: "2 a 4 anos", pct: 30, dias: 1460 },
      { label: "4 a 6 anos", pct: 25, dias: 2190 },
      { label: "6 a 8 anos", pct: 20, dias: 2920 },
      { label: "8 a 10 anos", pct: 15, dias: 3650 },
      { label: "Acima de 10 anos", pct: 10, dias: 9999 },
    ],
  },
  "Previdência (Tabela Progressiva)": {
    categoria: "Previdência",
    iof: false,
    comeCotas: false,
    nota: "Tabela progressiva IRPF no resgate. 15% retido na fonte, ajustável na declaração anual.",
    faixas: [
      { label: "Até R$ 2.259,20/mês", pct: 0, dias: 0 },
      { label: "R$ 2.259,21 a R$ 2.826,65", pct: 7.5, dias: 0 },
      { label: "R$ 2.826,66 a R$ 3.751,05", pct: 15.0, dias: 0 },
      { label: "R$ 3.751,06 a R$ 4.664,68", pct: 22.5, dias: 0 },
      { label: "Acima de R$ 4.664,68/mês", pct: 27.5, dias: 0 },
    ],
  },

  // ─── OUTROS ────────────────────────────────────────────────────────────
  Poupança: {
    categoria: "Outros",
    iof: false,
    comeCotas: false,
    nota: "Isenta de IR para pessoa física (Lei 8.981/1995).",
    faixas: [{ label: "Isento para PF", pct: 0, dias: 0 }],
  },
  Criptoativos: {
    categoria: "Outros",
    iof: false,
    comeCotas: false,
    nota: "15% sobre ganho de capital em vendas > R$ 35.000/mês (IN RFB 1.888/2019).",
    isencao: "Vendas ≤ R$ 35.000/mês: isento para PF",
    faixas: [{ label: "Ganho de capital", pct: 15.0, dias: 0 }],
  },
};

/**
 * IOF regressivo — Decreto 6.306/2007, Anexo.
 * Aplicável a renda fixa e fundos com resgate < 30 dias.
 * Índice = dia do resgate (1 a 30). Valor = % do IOF sobre o rendimento.
 */
export const IOF_REGRESSIVO = [
  96,
  93,
  90,
  86,
  83,
  80,
  76,
  73,
  70,
  66, // dias 1–10
  63,
  60,
  56,
  53,
  50,
  46,
  43,
  40,
  36,
  33, // dias 11–20
  30,
  26,
  23,
  20,
  16,
  13,
  10,
  6,
  3,
  0, // dias 21–30
] as const;

/**
 * Classes de alocação para simulador de aportes.
 * Usado em AportesPage.jsx.
 */
export const ALLOC_CLASSES: AllocClass[] = [
  {
    id: "reserva",
    label: "Reserva de Emergência",
    pct: 0,
    val: 0,
    cor: "#16A34A",
    desc: "Tesouro Selic / CDB DI liquidez diária",
  },
  {
    id: "pos",
    label: "Renda Fixa Pós (CDI / Selic)",
    pct: 0,
    val: 0,
    cor: "#0B2447",
    desc: "Tesouro Selic, CDB CDI, LCI/LCA",
  },
  {
    id: "ipca",
    label: "Renda Fixa IPCA+",
    pct: 0,
    val: 0,
    cor: "#1E40AF",
    desc: "Tesouro IPCA+, Debêntures IPCA",
  },
  {
    id: "pre",
    label: "Renda Fixa Pré-fixado",
    pct: 0,
    val: 0,
    cor: "#7C3AED",
    desc: "Tesouro Prefixado, CDB Pré",
  },
  {
    id: "credpriv",
    label: "Crédito Privado",
    pct: 0,
    val: 0,
    cor: "#D97706",
    desc: "CRI, CRA, Debêntures, FIDCs",
  },
  {
    id: "multi",
    label: "Multimercado",
    pct: 0,
    val: 0,
    cor: "#0891B2",
    desc: "Fundos multimercado / hedge",
  },
  {
    id: "rv",
    label: "Renda Variável BR",
    pct: 0,
    val: 0,
    cor: "#DC2626",
    desc: "Ações, FIIs, ETFs Brasil",
  },
  {
    id: "inter",
    label: "Internacional",
    pct: 0,
    val: 0,
    cor: "#C9A227",
    desc: "BDRs, ETFs globais, ações US",
  },
  { id: "prev", label: "Previdência", pct: 0, val: 0, cor: "#64748B", desc: "PGBL / VGBL" },
];

/**
 * Perfis de alocação pré-definidos — baseados em:
 * · Classificação ANBIMA de perfil de investidor
 * · Resolução CVM 30 (suitability)
 * · Alocação estratégica de longo prazo (Markowitz adaptado ao Brasil)
 */
export const PERFIS_ALLOC: Record<string, TargetAllocation> = {
  Conservador: {
    reserva: 15,
    pos: 25,
    ipca: 20,
    pre: 10,
    credpriv: 5,
    multi: 10,
    rv: 5,
    inter: 0,
    prev: 10,
  },
  Moderado: {
    reserva: 10,
    pos: 15,
    ipca: 18,
    pre: 8,
    credpriv: 7,
    multi: 12,
    rv: 15,
    inter: 5,
    prev: 10,
  },
  Arrojado: {
    reserva: 5,
    pos: 10,
    ipca: 13,
    pre: 7,
    credpriv: 5,
    multi: 12,
    rv: 25,
    inter: 13,
    prev: 10,
  },
  Agressivo: {
    reserva: 5,
    pos: 5,
    ipca: 10,
    pre: 5,
    credpriv: 5,
    multi: 10,
    rv: 30,
    inter: 20,
    prev: 10,
  },
};

/** @deprecated Usar client.patrimonio.total do snapshot. Mantido para testes legados. */
export const TOTAL_CARTEIRA: number = 0;

/** Valores rápidos para simulador de aportes. */
export const QUICKS_APORTES = [5000, 10000, 25000, 50000, 100000] as const;

/**
 * Cenários de aposentadoria — retorno NOMINAL anual bruto esperado.
 * Usado em AposentadoriaPage.jsx.
 */
export const CENS_APOSENT: Record<string, RetirementScenario> = {
  Conservador: {
    ret: 10.5,
    cor: "#16A34A",
    desc: "100% Renda Fixa (CDI / Selic)",
    alocDesc: "Tesouro Selic, CDBs, LCI/LCA",
  },
  Moderado: {
    ret: 11.5,
    cor: "#D97706",
    desc: "60% RF + 30% RV + 10% Inter",
    alocDesc: "Multimercado, Ações, Debêntures",
  },
  Arrojado: {
    ret: 13.0,
    cor: "#7C3AED",
    desc: "40% RF + 40% RV + 20% Exterior",
    alocDesc: "Ações, FIIs, ETFs globais",
  },
  Agressivo: {
    ret: 15.0,
    cor: "#DC2626",
    desc: "20% RF + 50% RV + 30% Exterior",
    alocDesc: "Heavy equity, BDRs, S&P 500",
  },
};
